/**
 * RecordLinkTypesTab — Settings → Typy powiązań.
 *
 * CRUD ekran dla słownika `record_link_types` (typy powiązań między kartotekami:
 * kontrahent/pracownik/towar/dokument). Backend: `apps/api/app/api/record-link-types/`
 * (patrz docstringi `route.ts` / `[id]/route.ts` / `seed-defaults/route.ts`).
 *
 * Ta zakładka zarządza WYŁĄCZNIE słownikiem typów (encja `record_links` — konkretne
 * powiązania między dwiema kartotekami — jest osobnym torem pracy: `RecordLinksSection`
 * i pokrewne pliki, NIE dotykane tutaj).
 *
 * Gate: `crm_core.dictionaries.view` (widoczność listy) / `crm_core.dictionaries.manage`
 * (mutacje — create/edit/delete/seed-defaults). Super-user bypassuje — wzorzec z
 * `DictionariesTab` (`me.permissions.includes(...)` + `isSuperUser`). Namespace
 * `crm_core.dictionaries.*` reużyty świadomie po stronie backendu (typ powiązania
 * to rodzaj słownika, nie osobny permission namespace).
 *
 * Wiersze `isSystem=true` (obsadzone przez „Załaduj domyślne") NIE mogą być kasowane
 * (backend: 403 `CANNOT_DELETE_SYSTEM_TYPE`) — przycisk usuń jest disabled z hintem
 * zamiast ukryty, żeby user rozumiał dlaczego (a nie myślał że akcja zniknęła). Kod
 * (`code`) jest immutable dla WSZYSTKICH wierszy (systemowych i lokalnych) — edycja
 * etykiet/koloru/ikony/typów/aktywności/kolejności jest dozwolona nawet dla systemowych.
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog + useToast.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type RecordLinkOwnerType,
  type RecordLinkTypeDTO,
  useRecordLinkTypeMutations,
  useRecordLinkTypes,
} from '@/hooks/useRecordLinks';

import { RecordLinkTypeFormModal, type RecordLinkTypeFormValues } from './RecordLinkTypeFormModal';

const VIEW_PERM = 'crm_core.dictionaries.view';
const MANAGE_PERM = 'crm_core.dictionaries.manage';

// ─── Główny komponent ──────────────────────────────────────────────────────

export function RecordLinkTypesTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();

  const isSuperUser = me?.user.isSuperUser === true;
  const canView = isSuperUser || (me?.permissions.includes(VIEW_PERM) ?? false);
  const canManage = isSuperUser || (me?.permissions.includes(MANAGE_PERM) ?? false);

  if (!canView && !canManage) {
    return (
      <Card padding="md" data-testid="record-link-types-tab-no-permission">
        <p className="text-sm text-text-tertiary">
          {t('tenant.recordLinkTypes.list.noPermission')}
        </p>
      </Card>
    );
  }

  return <RecordLinkTypesList canManage={canManage} />;
}

// ─── Lista typów powiązań ───────────────────────────────────────────────────

function RecordLinkTypesList({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const mutations = useRecordLinkTypeMutations();

  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RecordLinkTypeDTO | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RecordLinkTypeDTO | null>(null);
  // Błąd 409/400 (kod zajęty / immutable) z ostatniej próby zapisu — pokazywany
  // inline w modalu (§4.12), analogicznie do `DictionariesTab`. Reset przy każdym
  // otwarciu/zamknięciu modala i tuż przed każdą próbą zapisu.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const listQ = useRecordLinkTypes(showInactive);

  // Stabilna referencja initialValues dla RecordLinkTypeFormModal — inline literal
  // odtworzony na każdym renderze powodowałby, że efekt resetujący pola w modalu
  // (zależny od `[open, initialValues]`) odpalałby się przy KAŻDYM re-renderze
  // rodzica (np. gdy `isPending` mutacji przełącza się po nieudanym zapisie),
  // czyszcząc właśnie wpisane wartości dokładnie w momencie błędu — patrz
  // analogiczny komentarz w `DictionariesTab`.
  const formInitialValues = useMemo<RecordLinkTypeFormValues>(
    () =>
      editing
        ? {
            code: editing.code,
            forwardLabel: editing.forwardLabel,
            reverseLabel: editing.reverseLabel ?? '',
            isSymmetric: editing.isSymmetric,
            cardinality: editing.cardinality,
            color: editing.color ?? '',
            icon: editing.icon ?? '',
            sourceTypes: editing.sourceTypes as RecordLinkOwnerType[],
            targetTypes: editing.targetTypes as RecordLinkOwnerType[],
            isActive: editing.isActive,
            sortOrder: editing.sortOrder,
          }
        : {
            code: '',
            forwardLabel: '',
            reverseLabel: '',
            isSymmetric: false,
            cardinality: 'many_to_many',
            color: '',
            icon: '',
            sourceTypes: [],
            targetTypes: [],
            isActive: true,
            sortOrder: 0,
          },
    [editing],
  );

  function handleCreate(values: RecordLinkTypeFormValues) {
    setServerErrors({});
    mutations.createType.mutate(
      {
        code: values.code,
        forwardLabel: values.forwardLabel,
        reverseLabel: values.isSymmetric ? null : values.reverseLabel.trim() || null,
        isSymmetric: values.isSymmetric,
        cardinality: values.cardinality,
        color: values.color.trim() || null,
        icon: values.icon.trim() || null,
        sourceTypes: values.sourceTypes,
        targetTypes: values.targetTypes,
        isActive: values.isActive,
        sortOrder: values.sortOrder,
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.recordLinkTypes.toast.created') });
          setCreateOpen(false);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          if (code === 'CODE_TAKEN') {
            toast.show({ variant: 'warning', title: t('tenant.recordLinkTypes.toast.codeTaken') });
            setServerErrors({ code: t('tenant.recordLinkTypes.toast.codeTaken') });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.recordLinkTypes.toast.createError') });
        },
      },
    );
  }

  function handleUpdate(id: string, values: RecordLinkTypeFormValues) {
    mutations.updateType.mutate(
      {
        id,
        values: {
          forwardLabel: values.forwardLabel,
          reverseLabel: values.isSymmetric ? null : values.reverseLabel.trim() || null,
          isSymmetric: values.isSymmetric,
          cardinality: values.cardinality,
          color: values.color.trim() || null,
          icon: values.icon.trim() || null,
          sourceTypes: values.sourceTypes,
          targetTypes: values.targetTypes,
          isActive: values.isActive,
          sortOrder: values.sortOrder,
        },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.recordLinkTypes.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          if (code === 'CODE_IMMUTABLE') {
            toast.show({
              variant: 'warning',
              title: t('tenant.recordLinkTypes.toast.codeImmutable'),
            });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.recordLinkTypes.toast.updateError') });
        },
      },
    );
  }

  function handleDelete(row: RecordLinkTypeDTO) {
    mutations.deleteType.mutate(row.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.recordLinkTypes.toast.deleted') });
        setPendingDelete(null);
      },
      onError: (err) => {
        const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
        if (code === 'CANNOT_DELETE_SYSTEM_TYPE') {
          toast.show({
            variant: 'warning',
            title: t('tenant.recordLinkTypes.toast.cannotDeleteSystem'),
          });
          setPendingDelete(null);
          return;
        }
        if (code === 'LINK_TYPE_IN_USE') {
          toast.show({
            variant: 'warning',
            title: t('tenant.recordLinkTypes.toast.linkTypeInUse'),
          });
          setPendingDelete(null);
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.recordLinkTypes.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  function handleSeedDefaults() {
    mutations.seedDefaults.mutate(undefined, {
      onSuccess: (result) => {
        toast.show({
          variant: 'success',
          title: t('tenant.recordLinkTypes.toast.seedDefaultsSuccess', {
            inserted: result.inserted,
            skipped: result.skipped,
          }),
        });
      },
      onError: () => {
        toast.show({
          variant: 'error',
          title: t('tenant.recordLinkTypes.toast.seedDefaultsError'),
        });
      },
    });
  }

  if (listQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const rows = listQ.data ?? [];

  return (
    <div data-testid="record-link-types-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.recordLinkTypes.list.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('tenant.recordLinkTypes.list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Checkbox
            label={t('tenant.recordLinkTypes.list.showInactive')}
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="record-link-types-show-inactive"
          />
          {canManage && (
            <>
              <Button
                variant="secondary"
                leftIcon={<RefreshCw className="h-4 w-4" />}
                loading={mutations.seedDefaults.isPending}
                onClick={handleSeedDefaults}
                data-testid="record-link-types-seed-defaults-btn"
              >
                {t('tenant.recordLinkTypes.list.seedDefaults')}
              </Button>
              <Button
                variant="primary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setServerErrors({});
                  setCreateOpen(true);
                }}
                data-testid="record-link-types-new-btn"
              >
                {t('tenant.recordLinkTypes.list.newType')}
              </Button>
            </>
          )}
        </div>
      </header>

      {rows.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.recordLinkTypes.list.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.recordLinkTypes.list.col.forwardLabel')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.recordLinkTypes.list.col.reverseLabel')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.recordLinkTypes.list.col.cardinality')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.recordLinkTypes.list.col.color')}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t('tenant.recordLinkTypes.list.col.status')}
                  </th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {rows.map((row) => (
                  <tr key={row.id} data-testid={`record-link-type-row-${row.code}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-text-primary">{row.forwardLabel}</div>
                      <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-tertiary">
                        {row.code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {row.isSymmetric ? (
                        <span className="text-xs text-text-tertiary">
                          {t('tenant.recordLinkTypes.list.symmetricHint')}
                        </span>
                      ) : (
                        (row.reverseLabel ?? '—')
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {t(`tenant.recordLinkTypes.cardinality.${row.cardinality}`)}
                    </td>
                    <td className="px-4 py-3">
                      {row.color ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-4 w-4 rounded border border-border-primary"
                            style={{ backgroundColor: row.color }}
                          />
                          <span className="text-xs text-text-tertiary">{row.color}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {row.isActive ? (
                          <Badge color="green">
                            {t('tenant.recordLinkTypes.list.activeBadge')}
                          </Badge>
                        ) : (
                          <Badge color="gray">
                            {t('tenant.recordLinkTypes.list.inactiveBadge')}
                          </Badge>
                        )}
                        {row.isSystem && (
                          <Badge
                            color="accent"
                            data-testid={`record-link-type-system-badge-${row.code}`}
                          >
                            {t('tenant.recordLinkTypes.list.systemBadge')}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {canManage && (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setServerErrors({});
                              setEditing(row);
                            }}
                            aria-label={t('common.edit')}
                            className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                            data-testid={`record-link-type-edit-${row.code}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={row.isSystem}
                            onClick={() => setPendingDelete(row)}
                            aria-label={t('common.delete')}
                            title={
                              row.isSystem
                                ? t('tenant.recordLinkTypes.list.systemDeleteDisabledHint')
                                : undefined
                            }
                            className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted"
                            data-testid={`record-link-type-delete-${row.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {canManage && (
        <RecordLinkTypeFormModal
          open={createOpen || editing !== null}
          mode={editing ? 'edit' : 'create'}
          initialValues={formInitialValues}
          serverErrors={serverErrors}
          saving={mutations.createType.isPending || mutations.updateType.isPending}
          onSubmit={(values) => {
            if (editing) {
              handleUpdate(editing.id, values);
            } else {
              handleCreate(values);
            }
          }}
          onClose={() => {
            setCreateOpen(false);
            setEditing(null);
            setServerErrors({});
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.recordLinkTypes.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.recordLinkTypes.confirmDelete.message', {
                name: pendingDelete.forwardLabel,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        confirming={mutations.deleteType.isPending}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
